import {
  Card,
  Grid,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List as MuiList,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Button as MuiButton,
  MenuItem,
  TextField as MuiTextField,
} from "@mui/material";
import CardContent from "@mui/material/CardContent/CardContent";
import * as React from "react";
import { ReactNode } from "react";
import {
  List,
  Datagrid,
  TextField,
  SimpleShowLayout,
  Show,
  FunctionField,
  FilterLiveSearch,
  SavedQueriesList,
  useShowController,
  useListContext,
  TopToolbar,
  Button,
  ExportButton,
  usePermissions,
  useNotify,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { Cargo } from "../models/Cargo";
import { Address, OrderEventInfo } from "../models/common";
import { Waybill } from "../models/Waybill";
import { formatDate } from "../utils/common";
import { CargoCard } from "./components/cargo-card";
import {
  AsideStyledCard,
  fixedRightAsideSx,
  fixedRightAsideListSx,
} from "./components/common";
import {
  OrderStatus,
  getOrderStatusTitle,
  getOrderStatusColor,
  getOrderStatusChoices,
} from "../constants/OrderStatus";

// 添加图片导入
import errorIcon from '../static/images/icon_waybill_error.png';

// 添加导入
import WaybillImagesDialog from './waybills/waybill_images_dialog';
import WaybillSettlementDialog from './waybills/waybill_settlement_dialog';
import ImageIcon from '@mui/icons-material/Image';
import { useState, useMemo, useEffect } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getOrderEventTitle } from "../constants/OrderEventType";

/** 状态筛选「全部」哨兵值，请求时不传 status。 */
const STATUS_ALL_SENTINEL = OrderStatus.ALL.code;

const orderStatusFilterChoices = [
  { value: STATUS_ALL_SENTINEL, label: "全部状态" },
  ...getOrderStatusChoices().map((item) => ({
    value: item.id,
    label: item.name,
  })),
];

const Aside = () => (
  <AsideStyledCard sx={fixedRightAsideSx}>
    <CardContent>
      <FilterLiveSearch source="cargo.name" label="货物名称" alwaysOn />
      <FilterLiveSearch source="truck.plate" label="货车牌照" alwaysOn />
      <SavedQueriesList />
    </CardContent>
  </AsideStyledCard>
);

// 订单更新异常时间（单位：天）
const updateErrorDurationDays = 2;

// 添加检查函数
const checkNeedsUpdate = (events: OrderEventInfo[]): boolean => {
  if (!events || events.length === 0) return false;
  
  const latestEvent = events[events.length - 1];
  if (latestEvent.type !== 1 && latestEvent.type !== 3) return false;

  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(twoDaysAgo.getDate() - updateErrorDurationDays);
  
  const eventDate = new Date(latestEvent.time);
  return eventDate < twoDaysAgo;
};

const shouldShowDrivingTrackButton = (status: number): boolean => {
  return status === OrderStatus.DELIVERED.code || status === OrderStatus.COMPLETED.code;
};

// 修改批量下载按钮组件
const BatchDownloadButton = () => {
  const { selectedIds, data } = useListContext();
  
  const handleBatchDownload = () => {
    if (!data || !selectedIds || selectedIds.length === 0) return;
    
    // 从当前列表数据中过滤出选中的运单
    const selectedWaybills = Object.keys(data)
      .map((key: string) => data[key as keyof typeof data])
      .filter((waybill: Waybill) => selectedIds.includes(waybill.id) && waybill.status === 4);
    console.log("Completed orders = " + selectedWaybills.length);
  
    // 初始化工作表
    const ws = XLSX.utils.aoa_to_sheet([]);
    // 格式化当前日期为 yyyy-MM-dd HH:mm:ss
    const todayDate = new Date().toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/\//g, '-');
    const titleCells = ["订单号","货物名称", "货物单价(元)", "订金(元)", "订单总额", "发货地", "发货方", "发货方电话", "收货地", "收货方", "收货方电话", "货主", "货主ID", "司机", "司机号码", "司机ID", "车牌号", "车型", "预估重量(吨)", "实载重量(吨)", "实到重量(吨)", "实收重量(吨)","合同版本号", "付款方式","订单回单照片地址", "状态简报"];
    // 动态添加事件记录
    
    // 设置列宽
    const cellsWidth = [{ wch: 25 }];
    titleCells.forEach((cell) => {
      if (cell !== "订单号") {
        if (cell === "发货地" || cell === "收货地") {
          cellsWidth.push({ wch: 40 });
        } else if(cell === "货主ID" || cell === "司机ID"){
          cellsWidth.push({ wch: 25 });
        } else if (cell === "订单回单照片地址") {
          cellsWidth.push({ wch: 40 });
        } else if(cell === "状态简报") {
          cellsWidth.push({ wch: 80 });
        } else {
          cellsWidth.push({ wch: 12 });
        }
      }
    });
    
    ws['!cols'] = cellsWidth;
    const baseRows = [titleCells];
    
    // 遍历下载选中的
    selectedWaybills.forEach((waybill: Waybill) => {
     const events = waybill.events || [];
     var statusDesc = "";
     for (let i = 0; i < events.length; i++) {
      const event = events[i];
      statusDesc += "{" + formatDate(event.time) + " : " + getOrderEventTitle(event.type) + "}";
      if (i < events.length - 1) {
        statusDesc += " -> ";
      }
     }
      baseRows.push([
        waybill.id,
        waybill.cargo.name,
        waybill.cargo.single_price != null ? waybill.cargo.single_price + "元/车" : waybill.cargo.unit_price + "元/吨",
        waybill.cargo.deposit + "",
        waybill.cargo.single_price!= null? waybill.cargo.single_price + "" : Number((waybill.cargo.unit_price * (waybill.weights.actual_receive || 0)).toFixed(2)) + "",
        waybill.from.province + waybill.from.city + waybill.from.district + waybill.from.address,
        waybill.from.name,
        waybill.from.phone,
        waybill.to.province + waybill.to.city + waybill.to.district + waybill.to.address,
        waybill.to.name,
        waybill.to.phone,
        waybill.owner.name,
        waybill.owner._id,
        waybill.driver.name,
        waybill.driver.phone,
        waybill.driver._id,
        waybill.truck.plate,
        waybill.truck.type,
        waybill.cargo.expect_load || 0,
        waybill.weights.actual_depart || 0,
        waybill.weights.actual_arrive || 0,
        waybill.weights.actual_receive || 0,
        waybill.agreement_version || "3",
        waybill.offline_amount || false ? "线下支付: " : "线上支付: ",
        waybill.receipt || "无回单照片",
        statusDesc,
      ]);
    });
    // 将所有数据添加到工作表
    XLSX.utils.sheet_add_aoa(ws, baseRows, { origin: "A1" });
    // --------------------------
    // 生成并下载文件
    // --------------------------
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "订单-电子回单");

    const wbout = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        bookSST: true
    });

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "订单-" + todayDate + ".xlsx");
  };
  
  return (
    <Button
      onClick={handleBatchDownload}
      label="批量下载"
      disabled={!selectedIds || selectedIds.length === 0}
      startIcon={<DownloadIcon />}
      sx={{
        color: 'white',
        '& .MuiButton-startIcon': {
          color: 'white'
        }
      }}
    />
  );
};

/**
 * 解析列表筛选中的订单状态值。
 */
const parseStatusFilterValue = (value: unknown): number => {
  if (value == null || value === "") {
    return STATUS_ALL_SENTINEL;
  }
  const code = Number(value);
  if (Number.isNaN(code)) {
    return STATUS_ALL_SENTINEL;
  }
  const exists = orderStatusFilterChoices.some((item) => item.value === code);
  return exists ? code : STATUS_ALL_SENTINEL;
};

/**
 * 列表顶部操作栏：订单状态筛选 + 导出。
 */
const ListActions = () => {
  const { permissions } = usePermissions();
  const { filterValues, setFilters } = useListContext();
  const roles: string[] = (permissions as any)?.realm_access?.roles ?? [];
  const hasExport = roles.includes("export");
  const [draftStatus, setDraftStatus] = useState<number>(
    parseStatusFilterValue(filterValues.status)
  );

  useEffect(() => {
    setDraftStatus(parseStatusFilterValue(filterValues.status));
  }, [filterValues.status]);

  const handleSearch = () => {
    const nextFilters = {
      ...filterValues,
      status: draftStatus !== STATUS_ALL_SENTINEL ? draftStatus : undefined,
    };
    setFilters(nextFilters, undefined);
  };

  const handleReset = () => {
    setDraftStatus(STATUS_ALL_SENTINEL);
    const nextFilters = { ...filterValues } as Record<string, unknown>;
    delete nextFilters.status;
    setFilters(nextFilters, undefined);
  };

  return (
    <TopToolbar sx={{ width: "100%", alignItems: "center", gap: 1, flexWrap: "wrap", px: 2, py: 1 }}>
      <MuiTextField
        select
        size="small"
        label="订单状态"
        value={draftStatus}
        onChange={(event) => setDraftStatus(Number(event.target.value))}
        sx={{ minWidth: 180 }}
      >
        {orderStatusFilterChoices.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </MuiTextField>
      <MuiButton variant="contained" onClick={handleSearch}>
        查询
      </MuiButton>
      <MuiButton onClick={handleReset}>重置</MuiButton>
      {hasExport && <ExportButton />}
    </TopToolbar>
  );
};

const generateExcel = (data: Waybill) => {
    // 初始化工作表
    const ws = XLSX.utils.aoa_to_sheet([]);
    const fromAddress = data.from.province + data.from.city + data.from.district + data.from.address;
    const toAddress = data.to.province + data.to.city + data.to.district + data.to.address;
    const paymentWay = data.offline_amount || false ? "线下支付: " : "线上支付: ";
    var orderAmount = 0;
    var expectLoad = 0;
    var actualDepart = 0;
    var actualArrive = 0;
    var actualReceive = 0;
    var ownerName = "货主";
    var ownerId = data.cargo.owner;
    var driverName = "司机";
    var driverId = "司机ID";
    if (data.cargo.single_price != null) {
      orderAmount = data.cargo.single_price;
    } else {
      try {
        // 保留2位小数
        orderAmount = Number((data.cargo.unit_price * (data.weights.actual_receive || 0)).toFixed(2));
      } catch (error) {
        orderAmount = 0;
      }
    }
    expectLoad = data.cargo.expect_load || 0;
    actualDepart = data.weights.actual_depart || 0;
    actualArrive = data.weights.actual_arrive || 0;
    actualReceive = data.weights.actual_receive || 0;

    ownerName = data.owner.name;
    ownerId = data.owner._id;
    driverName = data.driver.name;
    driverId = data.driver._id;
    // 准备基础数据行
    const baseRows = [
        ["合同版本："+ data.agreement_version || "3", "", "", "桂运宝电子回单", "", "订单号：" + data.id, "", ""],
        ["发货方：", "", "", "0772-2991568", "", "收货方：", "", ""],
        [data.from.name, data.from.phone, "", "", "", data.to.name, data.to.phone, "", ""],
        [fromAddress, "", "", "", "", toAddress, "", ""],
        ["货物明细：", "", "", "", "", "", paymentWay + orderAmount + "元", ""],
        ["货物名称", "", "预估重量(吨)", "实载重量(吨)", "实到重量(吨)", "实收重量(吨)", "订金(元)", "按吨计费"],
        [data.cargo.name, "", expectLoad, actualDepart, actualArrive, actualReceive, data.cargo.deposit, data.cargo.unit_price],
        ["", "", "", "", "", "", "", ""],
        ["货主", ownerName, "", "司机", driverName, "", "货车", data.truck.plate],
        ["货主", ownerId, "", "司机", driverId, "", "货车", data.truck.type],
        ["", "", "", "", "", "", "", ""],
    ];
    const baseRowsLength = baseRows.length;
    // 动态添加事件记录
    if (data.events && data.events.length > 0) {
        data.events.forEach(event => {
            baseRows.push(["状态简报", "",
                formatDate(event.time), // 格式化时间
                "", "", 
                getOrderEventTitle(event.type),//event.type.toString(),  // 事件类型
                "", ""
            ]);
        });
    }
    // 回单图片
    baseRows.push(["回单图片", "", data.receipt || "无回单照片", "", "", "", "", ""]);

    // 将所有数据添加到工作表
    XLSX.utils.sheet_add_aoa(ws, baseRows, { origin: "A1" });

    // 设置列宽
    ws['!cols'] = [
        { wch: 15 }, // A列 - 15个字符宽
        { wch: 12 }, // B列
        { wch: 12 }, // C列
        { wch: 15 }, // D列
        { wch: 12 }, // E列
        { wch: 12 }, // F列
        { wch: 12 }, // G列
        { wch: 20 }, // H列 - 订单号列宽一些
    ];
    const mergeCells = [
      // 第一行合并单元格
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 0, c: 3 }, e: { r: 0, c: 4 } },
      { s: { r: 0, c: 5 }, e: { r: 0, c: 7 } },
      // 第二行合并单元格
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } }, // 发货方
      { s: { r: 1, c: 3 }, e: { r: 1, c: 4 } }, // 电话号码
      { s: { r: 1, c: 5 }, e: { r: 1, c: 7 } },  // 收货方
      // 第三行合并单元格
      { s: { r: 2, c: 1 }, e: { r: 2, c: 2 } }, // 发货人信息
      { s: { r: 2, c: 6 }, e: { r: 2, c: 7 } },  // 收货人信息
      // 第四行合并单元格
      { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } }, // 发货地址
      { s: { r: 3, c: 5 }, e: { r: 3, c: 7 } },  // 收货地址
      // 第五行合并单元格
      { s: { r: 4, c: 0 }, e: { r: 4, c: 5 } }, // 货物明细
      { s: { r: 4, c: 6 }, e: { r: 4, c: 7 } },  // 金额
      // 第六行
      { s: { r: 5, c: 0 }, e: { r: 5, c: 1 } }, // 货物名称
      // 第七行
      { s: { r: 6, c: 0 }, e: { r: 6, c: 1 } }, // 详情信息
      // 第9行
      { s: { r: 8, c: 0 }, e: { r: 9, c: 0 } }, // 
      { s: { r: 8, c: 1 }, e: { r: 8, c: 2 } }, // 
      { s: { r: 9, c: 1 }, e: { r: 9, c: 2 } }, // 
      { s: { r: 8, c: 3 }, e: { r: 9, c: 3 } }, // 
      { s: { r: 8, c: 4 }, e: { r: 8, c: 5 } }, // 
      { s: { r: 9, c: 4 }, e: { r: 9, c: 5 } }, // 
      { s: { r: 8, c: 6 }, e: { r: 9, c: 6 } }, // 
    ];
    // 动态添加事件表格
    if (data.events && data.events.length > 0) {
      const events = data.events || [];
      const eventsLenght = events.length;
      mergeCells.push({
        s: { r: baseRowsLength, c: 0 },
        e: { r: baseRowsLength + eventsLenght - 1, c: 1 }
      });
      events.forEach((_, index) => {
        const rowIndex = index + baseRowsLength;
        mergeCells.push({
          s: { r: rowIndex, c: 2 },
          e: { r: rowIndex, c: 4 }
        });
        mergeCells.push({
          s: { r: rowIndex, c: 5 },
          e: { r: rowIndex, c: 7 }
        })
      })
    }
    const totalRows = baseRows.length;
    // 回单图片
    mergeCells.push({
      s: { r: totalRows - 1, c: 0 },
      e: { r: totalRows - 1, c: 1 }
    });
    mergeCells.push({
      s: { r: totalRows - 1, c: 2 },
      e: { r: totalRows - 1, c: 7 }
    });

    // 合并单元格
    ws["!merges"] = mergeCells;

    // 设置样式
    ['A1', 'D1', 'G1', 'A2', 'D2', 'G2', 'A3', 'B3', 'F3', 'G3', 'A4', 'F4'].forEach(cell => {
        ws[cell].s = {
            alignment: {
                horizontal: 'center',
                vertical: 'center'
            },
            font: {
                bold: true,
                sz: 12
            }
        };
    });

    // --------------------------
    // 生成并下载文件
    // --------------------------
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "订单-电子回单");

    const wbout = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        bookSST: true
    });

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "订单-" + data.id + ".xlsx");
};

const CargoCompact = ({ cargo }: { cargo: Cargo }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
    <Typography
      variant="body2"
      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", color: "text.primary" }}
    >
    </Typography>
    {cargo?.image ? (
      <Box
        component="img"
        src={cargo.image}
        sx={{
          width: 108,
          height: 108,
          borderRadius: 1,
          flexShrink: 0,
          objectFit: "cover",
          bgcolor: "grey.100",
        }}
      />
    ) : (
      <Box
        sx={{
          width: 108,
          height: 108,
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: "grey.100",
        }}
      />
    )}
  </Box>
);

const WaybillSummaryCell = ({ record }: { record: Waybill }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "160px 1fr",
      columnGap: 1.5,
      alignItems: "start",
      minWidth: 0,
    }}
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          color: "text.primary",
        }}
        title={`${record?.id ?? ""}`}
      >
        {record?.id ?? "-"}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.primary", mt: "5px" }}>
        货物名称：{record?.cargo?.name ?? "-"}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.primary", mt: "5px" }}>
        货主：{record?.owner?.name ?? "-"}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.primary", mt: "10px"  }}>
        发布/更新时间：<br />{record?.cargo?.updatedAt ? formatDate(record.cargo.updatedAt) : "-"}
      </Typography>
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <CargoCompact cargo={record.cargo} />
    </Box>
  </Box>
);

const WaybillRouteTimeCell = ({ record }: { record: Waybill }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
    <Typography variant="body2" noWrap title={`${record?.from?.city ?? ""} → ${record?.to?.city ?? ""}`}>
      {(record?.from?.city ?? "-") + " → " + (record?.to?.city ?? "-")}
    </Typography>
    <Typography variant="caption" sx={{ color: "text.primary" }}>
      装车：{record?.cargo?.expect_departure_at ? formatDate(record.cargo.expect_departure_at) : "-"}
    </Typography>
  </Box>
);

const WaybillDriverTruckCell = ({ record }: { record: Waybill }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
    <Typography
      variant="body2"
      noWrap
      title={`${record?.driver?.name ?? ""} ${record?.driver?.phone ?? ""}`}
      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {(record?.driver?.name ?? "-") + (record?.driver?.phone ? `（${record.driver.phone}）` : "")}
    </Typography>
    <>
      <Typography
        variant="body2"
        sx={{ color: "text.primary", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        noWrap
        title={`${record?.truck?.plate ?? ""}`}
      >
        车牌号：{record?.truck?.plate ?? "-"}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "text.primary", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        noWrap
        title={`${record?.truck?.type ?? ""}`}
      >
        车长车型：{record?.truck?.type ?? ""}
      </Typography>
    </>
  </Box>
);

const WaybillStatusCell = ({ record }: { record: Waybill }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const sortedEvents = useMemo(() => {
    const events = record.events || [];
    return [...events].sort((a, b) => {
      const aTime = a?.time ? Date.parse(a.time) : Number.NaN;
      const bTime = b?.time ? Date.parse(b.time) : Number.NaN;
      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return aTime - bTime;
    });
  }, [record.events]);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mt: "5px"}}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          label={getOrderStatusTitle(record.status)}
          clickable
          onClick={(e) => {
            e.stopPropagation();
            setIsStatusDialogOpen(true);
          }}
          size="small"
          sx={{
            backgroundColor: getOrderStatusColor(record.status),
            color: "white",
          }}
        />
        {checkNeedsUpdate(record.events) && (
          <img
            src={errorIcon}
            alt="更新异常"
            title="更新异常"
            style={{
              width: "16px",
              height: "16px",
              verticalAlign: "middle",
            }}
          />
        )}
      </Box>
      {shouldShowDrivingTrackButton(record.status) && (
        <MuiButton
          size="small"
          variant="text"
          onClick={async (e) => {
            e.stopPropagation();
            const ownerId = record?.owner?._id;
            if (!ownerId) {
              notify("缺少货主ID，无法查看行车轨迹", { type: "error" });
              return;
            }
            try {
              sessionStorage.setItem('gyb_waybills_scrollY', String(window.scrollY));
              sessionStorage.setItem('gyb_waybills_anchorId', record.id);
            } catch {}
            navigate(`/waybills/${record.id}/driving-track/${ownerId}`);
          }}
          sx={{ mt: 0.5, minWidth: 0, px: 0 }}
        >
          行车轨迹
        </MuiButton>
      )}
      <Dialog
        open={isStatusDialogOpen}
        onClose={() => setIsStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>状态更新记录</DialogTitle>
        <DialogContent dividers>
        <Typography>  运单号：{record.id}</Typography>
        <Typography>  货物名称：{record?.cargo?.name ?? ""}</Typography>
          {sortedEvents.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              暂无状态更新记录
            </Typography>
          ) : (
            <MuiList dense disablePadding>
              {sortedEvents.map((event, index) => (
                <ListItem key={`${event.type}-${event.time}-${index}`} disableGutters>
                  <ListItemText
                    primary={getOrderEventTitle(event.type)}
                    secondary={event.time ? `更新时间：${formatDate(event.time)}` : "更新时间：-"}
                  />
                </ListItem>
              ))}
            </MuiList>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsStatusDialogOpen(false);
            }}
          >
            关闭
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const WaybillList: React.FC = (props) => {
  const [selectedWaybill, setSelectedWaybill] = useState<Waybill | null>(null);
  const [settlementWaybill, setSettlementWaybill] = useState<Waybill | null>(null);
  const { permissions } = usePermissions();
  const roles: string[] = useMemo(
    () => (permissions as any)?.resource_access?.account?.roles ?? [],
    [permissions]
  );
  const hasFinancial = roles.includes('financial');

  useEffect(() => {
    const yStr = sessionStorage.getItem('gyb_waybills_scrollY');
    if (yStr) {
      sessionStorage.removeItem('gyb_waybills_scrollY');
      const y = parseInt(yStr, 10);
      if (!Number.isNaN(y)) {
        window.requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
      }
    }
  }, []);

  return (
    <List 
      {...props} 
      aside={<Aside />} 
      sort={{ field: "id", order: "DESC" }}
      actions={<ListActions />}
      sx={{
        "& .RaList-main": {
          marginRight: "17em",
        },
      }}
    >
      <Datagrid 
        rowClick="show"
        // 只有当记录状态为完成(假设完成状态值为4)时才显示批量操作按钮
        bulkActionButtons={ <BatchDownloadButton /> }
        sx={{
          "& .RaDatagrid-table": {
            tableLayout: "fixed",
            width: "100%",
          },
          "& .RaDatagrid-thead .MuiTableCell-root": {
            verticalAlign: "middle",
            py: 1.25,
            overflow: "visible",
          },
          "& .RaDatagrid-thead .MuiTableCell-paddingCheckbox, & .RaDatagrid-rowCell.MuiTableCell-paddingCheckbox": {
            width: 52,
            px: 1.25,
            boxSizing: "border-box",
          },
          "& .RaDatagrid-thead .MuiCheckbox-root": {
            my: 0,
          },
          "& .RaDatagrid-rowCell": {
            verticalAlign: "top",
            py: 0.75,
          },
          "& .column-summary": { width: "35%" },
          "& .column-driverAndTruck": { width: "22%" },
          "& .column-routeAndTime": { width: "19%" },
          "& .column-statusSummary": { width: "14%" },
          "& .column-rowActions": { width: "10%" },
        }}
      >
        <FunctionField
          source="summary"
          label="运单"
          sortBy="id"
          render={(record: Waybill) => <WaybillSummaryCell record={record} />}
        />
        <FunctionField
          source="driverAndTruck"
          label="司机/车辆"
          render={(record: Waybill) => <WaybillDriverTruckCell record={record} />}
        />
        <FunctionField
          source="routeAndTime"
          label="线路/时间"
          sortBy="cargo.expect_departure_at"
          render={(record: Waybill) => <WaybillRouteTimeCell record={record} />}
        />
        <FunctionField
          source="statusSummary"
          label="状态"
          render={(record: Waybill) => <WaybillStatusCell record={record} />}
        />
        <FunctionField
          source="rowActions"
          label="操作"
          render={(record: Waybill) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedWaybill(record);
                }}
                size="small"
                color="primary"
                title="查看图片"
              >
                <ImageIcon />
              </IconButton>
              {record.status === 4 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    generateExcel(record);
                  }}
                  size="small"
                  color="secondary"
                  title="下载订单"
                >
                  <GetAppIcon />
                </IconButton>
              )}
              {record.status === 3 && hasFinancial && (
                <MuiButton
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSettlementWaybill(record);
                  }}
                >
                  结算
                </MuiButton>
              )}
            </Box>
          )}
        />
      </Datagrid>

      <WaybillImagesDialog
        open={!!selectedWaybill}
        onClose={() => setSelectedWaybill(null)}
        waybillId={selectedWaybill?.id || ""}
        truck={selectedWaybill?.truck || null}
        cargo={selectedWaybill?.cargo || null}
        receiptImage={selectedWaybill?.receipt || null}
      />
      <WaybillSettlementDialog
        open={!!settlementWaybill}
        onClose={() => setSettlementWaybill(null)}
        waybill={settlementWaybill}
        onConfirm={() => {}}
      />
    </List>
  );
};

interface CardWithContentProps {
  children: ReactNode;
}

const CardWithContent = ({ children }: CardWithContentProps) => (
  <Card sx={{ maxWidth: 600 }}>
    <CardContent>{children}</CardContent>
  </Card>
);

export const WaybillShow: React.FC = (props) => {
  const { record } = useShowController(props);
  record.cargo.from = record.from as Address;
  record.cargo.to = record.to as Address;
  return (
    <Show {...props}>
      <SimpleShowLayout>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <CargoCard cargo={record?.cargo} />
          </Grid>
          <Grid item xs={12} md={8}>
            <CardWithContent>
              <Typography variant="h5" component="div">
                <TextField
                  source="truck.plate"
                  label="货车牌照"
                  record={record}
                />
              </Typography>
              <Typography variant="h5" component="div">
                <TextField
                  source="truck.type"
                  label="货车类型"
                  record={record}
                />
              </Typography>
              <Typography variant="h5" component="div">
                <TextField
                  source="truck.phone"
                  label="司机号码"
                  record={record}
                />
              </Typography>
            </CardWithContent>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};
